import { TranslateService } from "@ngx-translate/core";
import {
  ADD_ASSET,
  REMOVE_ASSET,
  UPDATE_ASSET,
} from "src/app/store/actions/asset.action";
import {
  SiteModel,
  AssetModel,
  MasterAsset,
  SubAssetModel,
  SapAssetsModel,
  AssetInformation,
  SiteFencingModel,
  ZoneFencingModel,
  AssetCategoryModel,
  MasterAssetResponseModel,
} from "src/app/store/models/asset.model";
import { Store } from "@ngrx/store";
import { Router } from "@angular/router";
import { Injectable, inject } from "@angular/core";
import { BehaviorSubject, Subject, take, takeUntil } from "rxjs";
import { LoadingController } from "@ionic/angular/standalone";
import { ToastService } from "../../toast-service/toast.service";
import { RoleAccessModel } from "src/app/store/models/role.model";
import { SiteSegmentModel } from "src/app/store/models/siteSegment.model";
import { HttpService } from "src/app/services/http-service/http-client.service";

import {
  processMasterAssets,
  getCurrentBogProModuleMap,
} from "src/app/utils/asset.util";

export type StepType = "AssetRegister" | "ReviewAndSubmit";

interface Stepper {
  title: string;
  type: StepType;
  enabled: boolean;
  iconActive: string;
  completed: boolean;
  iconEnabled: string;
  description?: string;
  iconCompleted: string;
}

@Injectable({ providedIn: "root" })
export class StructuralMapService {
  // ** Dependency Injection
  private readonly store = inject(Store);
  private readonly router = inject(Router);
  private translate = inject(TranslateService);
  private readonly httpService = inject(HttpService);
  private readonly toastService = inject(ToastService);
  private readonly loadingCtrl = inject(LoadingController);

  // ** Public Reactive State
  public groupedMasterAssets = new BehaviorSubject<
    { category: string; assets: MasterAsset[] }[]
  >([]);
  public site: SiteModel;
  draggedAsset: AssetModel;
  destroy$ = new Subject<void>();
  public backdrop: HTMLDivElement;
  bogProModules: AssetCategoryModel[] = [];
  public role: RoleAccessModel | undefined;
  public selectedAssets: AssetModel[] = [];
  public autoSubAssets: SubAssetModel[] = [];
  public isViewMode = new BehaviorSubject(false);
  public siteId = new BehaviorSubject<string>("");
  public isAssetSave = new BehaviorSubject(false);
  public isInfoModal = new BehaviorSubject(false);
  public isDeleteMode = new BehaviorSubject(false);
  public AssetClicked = new BehaviorSubject(false);
  public isApproverMode = new BehaviorSubject(false);
  public isSuccessModal = new BehaviorSubject(false);
  public IsAbleTOSubmit = new BehaviorSubject(false);
  public isCrossFromMap = new BehaviorSubject(false);
  public asset = new BehaviorSubject<AssetModel>({});
  public isSubAssetModal = new BehaviorSubject(false);
  public allAssetsHaveId = new BehaviorSubject(false);
  public isSapAssetModal = new BehaviorSubject(false);
  public isSaveAssetModal = new BehaviorSubject(false);
  public isParentSelected = new BehaviorSubject(false);
  public sapAssetCount: { [key: string]: number } = {};
  public assets = new BehaviorSubject<AssetModel[]>([]);
  public zoneToggleDisabled = new BehaviorSubject(false);
  public isMasterAssetModal = new BehaviorSubject(false);
  public reviewNextZone = new BehaviorSubject<string>("");
  public isSubAssetsSelected = new BehaviorSubject(false);
  public isDragMode = new BehaviorSubject<boolean>(false);
  public sapAssets = new BehaviorSubject<AssetModel[]>([]);
  public assetMarker = new BehaviorSubject<AssetModel>({});
  public isRejectModal = new BehaviorSubject<boolean>(false);
  public deleteParentAssetModal = new BehaviorSubject(false);
  public isAllAssetsBulkApprovel = new BehaviorSubject(false);
  public subAssetMarker = new BehaviorSubject<AssetModel>({});
  public isRejectReason = new BehaviorSubject<boolean>(false);
  public others = new BehaviorSubject<AssetInformation[]>([]);
  public masterAssets = new BehaviorSubject<MasterAsset[]>([]);
  private undoSelectedAssetsSubject = new Subject<AssetModel>();
  public allZonesReviewed = new BehaviorSubject<boolean>(false);
  public assetToBeDeleted = new BehaviorSubject<AssetModel>({});
  public crossMarkerListeners = new Map<string, EventListener>();
  public currentReviewZoneIndex = new BehaviorSubject<number>(0);
  public isStepperExpanded = new BehaviorSubject<boolean>(false);
  public isLoadingCompleted = new BehaviorSubject<boolean>(false);
  public recommended = new BehaviorSubject<AssetInformation[]>([]);
  public assetsForApproval = new BehaviorSubject<AssetModel[]>([]);
  public clearSubAssetSearch$ = new BehaviorSubject<boolean>(false);
  public isMasterAssetRecommendedModal = new BehaviorSubject(false);
  public activeStep = new BehaviorSubject<StepType>("AssetRegister");
  public sapAssetsMaster = new BehaviorSubject<SapAssetsModel[]>([]);
  public activeIndex = new BehaviorSubject({ index: -1, length: 0 });
  public isSelectedZoneComplete = new BehaviorSubject<boolean>(false);
  undoSelectedAssets$ = this.undoSelectedAssetsSubject.asObservable();
  public sendForApprovalAssets = new BehaviorSubject<AssetModel[]>([]);
  public isStructuralAssetSummary = new BehaviorSubject<boolean>(false);
  public isStructuralApprovalSummary = new BehaviorSubject<boolean>(false);
  public assetViewMode$ = new BehaviorSubject<"assetName" | "hacCode">(
    "assetName",
  );
  selectedAssetTypeAndTitle$ = new BehaviorSubject<{
    type: string;
    title: string;
  } | null>(null);
  private changeParentDesignSubject = new Subject<{
    asset: AssetModel;
    assetCount: number;
    parentSelected: boolean;
  }>();
  currentType = new BehaviorSubject<string>("");
  currentTitle = new BehaviorSubject<string>("");
  showSettings = new BehaviorSubject<boolean>(false);
  private deleteMarkerSubject = new Subject<AssetModel>();
  private updateAssetTypeSubject = new Subject<AssetModel>();
  updateAssetType$ = this.updateAssetTypeSubject.asObservable();
  deleteMarkerSubject$ = this.deleteMarkerSubject.asObservable();
  SPECIFICATION_ASSET_TYPES = ["silo", "buildings", "storage_hall"];
  changeParentDesign$ = this.changeParentDesignSubject.asObservable();
  openSapKeyCodeChangeConfirmation = new BehaviorSubject<boolean>(false);
  public selectedSidebarAssetOnClick = new BehaviorSubject<MasterAsset>({});
  private siteSegmentsList: string[] = [];

  stepper: Stepper[] = [
    {
      enabled: true,
      completed: false,
      type: "AssetRegister",
      title: this.translate.instant("STEPPER_REGISTER_ASSET"),
      iconActive:
        "../../../../assets/settings/structural/stepper/step1-active.svg",
      iconEnabled:
        "../../../../assets/settings/structural/stepper/step1-enabled.svg",
      iconCompleted:
        "../../../../assets/settings/structural/stepper/step1-completed.svg",
      description: `<ul class="space-y-[4px]">
                     <li class="relative pl-[16px]">
                      <span class="absolute left-0 top-0">●</span>
                      ${this.translate.instant("STEPPER_SELECT_ZONE_REGISTER")} <span class="font-nunito_bold">${this.translate.instant("STEPPER_DRAG_DROP")}</span> ${this.translate.instant("STEPPER_BIRDS_EYE_VIEW")} <span class="font-nunito_bold">${this.translate.instant("STEPPER_COMPLETE_SPEC")}</span>
                     </li>
                     <li class="relative pl-[16px]">
                      <span class="absolute left-0 top-0">●</span>
                      ${this.translate.instant("STEPPER_CHILD_ASSET")}
                     </li>
                    </ul>`,
    },
    {
      enabled: false,
      completed: false,
      type: "ReviewAndSubmit",
      title: this.translate.instant("STEPPER_REVIEW_AND_SUBMIT"),
      iconActive:
        "../../../../assets/settings/structural/stepper/step2-active.svg",
      iconEnabled:
        "../../../../assets/settings/structural/stepper/step2-enabled.svg",
      iconCompleted:
        "../../../../assets/settings/structural/stepper/step2-completed.svg",
      description: `<ul class="space-y-[4px]">
                     <li class="relative pl-[16px]">
                      <span class="absolute left-0 top-0">●</span>
                      ${this.translate.instant("STEPPER_REVIEW_INVENTORY")}
                     </li>
                     <li class="relative pl-[16px]">
                      <span class="absolute left-0 top-0">●</span>
                      ${this.translate.instant("STEPPER_SUBMIT_INVENTORY")}
                     </li>
                    </ul>
                   `,
    },
  ];

  constructor() {
    this.store
      .select("siteSegments")
      .subscribe((segments: SiteSegmentModel[]) => {
        this.siteSegmentsList = (segments ?? [])
          .filter((segment) => !(segment?.ignoreForFiltering ?? false))
          .map((segment) => segment?.segment ?? "");
      });
  }

  // *** This function will only be called based on condition during first time page load (onInit) ***
  setCompletedStepFirstTime(stepType: StepType): void {
    const stepIndex = this.stepper.findIndex((step) => step.type === stepType);
    this.stepper.forEach((step, idx) => {
      if (idx <= stepIndex) {
        step.enabled = true;
        step.completed = true;
      } else if (idx === stepIndex + 1) {
        step.enabled = true;
        step.completed = false;
      } else if (idx > stepIndex + 1) {
        step.enabled = false;
        step.completed = false;
      }
    });
    if (stepIndex + 1 < this.stepper.length) {
      this.activeStep.next(this.stepper[stepIndex + 1]?.type);
    } else {
      this.activeStep.next(this.stepper[stepIndex]?.type);
    }
  }

  isStepCompleted(stepType: StepType): boolean {
    const step = this.stepper.find((s) => s.type === stepType);
    return step ? step.completed : false;
  }

  // ** Method: Undo Selected Assets
  requestDeleteMarker(asset: AssetModel) {
    this.deleteMarkerSubject.next(asset);
  }

  // ** Method: Undo Selected Assets
  requestUndoSelectedAssets(asset: AssetModel) {
    this.undoSelectedAssetsSubject.next(asset);
  }

  // ** Method: Update Asset Type
  requestUpdateAssetType(asset: AssetModel) {
    this.updateAssetTypeSubject.next(asset);
  }

  // ** Method: Change Parent Asset Marker
  requestChangeParentAssetDesign(
    asset: AssetModel,
    assetCount: number,
    parentSelected: boolean,
  ) {
    this.changeParentDesignSubject.next({ asset, assetCount, parentSelected });
  }

  // ** Method: Convert Point to LatLng
  public PointToLatLng(point: any, map: google.maps.Map): google.maps.LatLng {
    const gmap: any = map;
    const topRight = gmap
      .getProjection()
      .fromLatLngToPoint(gmap.getBounds().getNorthEast());
    const bottomLeft = gmap
      .getProjection()
      .fromLatLngToPoint(gmap.getBounds().getSouthWest());
    const scale = Math.pow(2, gmap.getZoom());
    const worldPoint = new google.maps.Point(
      point.x / scale + bottomLeft.x,
      point.y / scale + topRight.y,
    );
    return gmap.getProjection().fromPointToLatLng(worldPoint);
  }

  // ** Method: Check if LatLng is inside Polygon
  public isPointInZone(
    latLng: google.maps.LatLng,
    polygon: google.maps.Polygon,
  ): boolean {
    return google.maps.geometry.poly.containsLocation(latLng, polygon);
  }

  // ** Method: Find Zone for given LatLng
  public findZone(
    latLng: google.maps.LatLng,
    zoneCoordinates: ZoneFencingModel[],
  ): string {
    for (const zone of zoneCoordinates) {
      const polygon = new google.maps.Polygon({ paths: zone.coordinates });
      if (google.maps.geometry.poly.containsLocation(latLng, polygon)) {
        return zone.id;
      }
    }
    return "";
  }

  // ** Method: Get Asset by ID
  public GetAsset(id: string, assets: AssetModel[]): AssetModel {
    return assets?.find((asset) => asset.id === id || asset.uid === id) || {};
  }

  // ** Method: Get Sub Assets
  public GetSubAssets(pid: string) {
    let subAssets: SubAssetModel[] = [];
    let autoAssets: SubAssetModel[] = [];

    this.store
      .select("site")
      .pipe(take(1))
      .subscribe({
        next: (site: SiteModel) => {
          if (site && site.assets) {
            subAssets = site.assets?.filter((asset) => asset.pid === pid);
            autoAssets = this.autoSubAssets?.filter(
              (asset) => asset.pid === pid,
            );
            subAssets = [...autoAssets, ...subAssets];
          }
        },
      });

    return subAssets || [];
  }

  // ** Method: Check if Asset and Sub Assets are Rejected
  public checkRejectAssets(parentUid: string): boolean {
    if (!parentUid) return false;

    const parent = this.GetAssetByUid(parentUid);
    const subAssets = this.GetSubAssets(parentUid);

    return (
      !!parent?.assetStatus?.status.assetRejected &&
      (subAssets && subAssets?.length
        ? subAssets.every((a) => a.assetStatus?.status.assetRejected)
        : false)
    );
  }

  // ** Method: Check if Asset and Sub Assets are Approved
  // public checkApprovedAssets(parentUid: string): boolean {
  //   if (!parentUid) return false;

  //   const parent = this.GetAssetByUid(parentUid);
  //   const subAssets = this.GetSubAssets(parentUid);

  //   return (
  //     !!parent?.assetStatus?.status.assetRegistered &&
  //     (subAssets && subAssets?.length
  //       ? subAssets.every((a) => a.assetStatus?.status.assetRegistered)
  //       : true)
  //   );
  // }

  // ** Method: Get Asset By UID
  public GetAssetByUid(uid: string): AssetModel | undefined {
    let asset: AssetModel | undefined;

    this.store.select("site").subscribe({
      next: (site: SiteModel) => {
        if (site && site.assets) {
          asset = site.assets.find((asset) => asset.uid === uid);
        }
      },
    });

    return asset;
  }

  public isSubAssetComplete(subAsset: SubAssetModel): boolean {
    return !!(
      subAsset?.id &&
      subAsset.assetInfo?.assetStatus &&
      subAsset.assetInfo?.assetCostCenter &&
      subAsset.assetInfo?.yearOfConstruction
    );
  }

  // ** Method: Check Sub Assets Completion
  public checkSubAssets(parentUid: string, assets: AssetModel[]): boolean {
    if (!parentUid) return false;

    const subAssets = this.GetSubAssets(parentUid);

    return subAssets && subAssets?.length
      ? subAssets.every((asset) => asset.assetStatus?.isRegistered)
      : false;
  }

  public hasSpecificationData = (asset: AssetModel) => {
    let hasSpecification = true;
    let assetSpecification = asset?.assetStructural;

    if (
      !asset?.assetStatus?.isRegistered &&
      asset?.assetInfo?.assetType &&
      this.SPECIFICATION_ASSET_TYPES.includes(asset.assetInfo.assetType)
    ) {
      if (!assetSpecification || typeof assetSpecification !== "object")
        return false;
      return Object.keys(assetSpecification).length > 0;
    }
    return true;
  };

  // ** Method: Check if Parent and Child Assets are Registered
  public isParentAndChildRegistered(parent: AssetModel): boolean {
    if (!parent?.id) return false;
    const subAssets = this.GetSubAssets(parent.uid!);

    const isParentComplete = parent.assetStatus?.isRegistered;

    if (!subAssets || !subAssets?.length) return !!isParentComplete;
    return !!isParentComplete && this.checkSubAssets(parent.uid!, subAssets);
  }

  // ** Method: Handle Marker Click on Map
  public handleMarkerClick(
    event: Event,
    assets: AssetModel[],
    mapMarker: HTMLElement,
    markerContainer: HTMLElement,
    polygon: google.maps.Polygon,
    coordinate: ZoneFencingModel[],
  ) {
    if (markerContainer.getAttribute("data-long-press-triggered") === "true") {
      return;
    }
    const uid = mapMarker.getAttribute("id")!;
    const asset = this.GetAsset(uid, assets);
    const latlng = new google.maps.LatLng(
      asset?.assetInfo?.assetZone?.coordinates!,
    );

    this.asset.next(asset);
    this.assetMarker.next(asset);
    this.isSubAssetModal.next(true);
    this.isSapAssetModal.next(false);
    this.GetOthersMasterAssets(asset);
    this.GetRecommendedMasterAssets(asset);
    this.isMasterAssetRecommendedModal.next(true);
  }

  // ** Method: Handle Marker Drag Start
  public handleMarkerDragEnd(
    mapMarker: HTMLElement,
    markerContainer: HTMLElement,
    zoneCoordinates: ZoneFencingModel[],
    marker: google.maps.marker.AdvancedMarkerElement,
  ) {
    this.isDragMode.next(false);
    const latlng = new google.maps.LatLng(
      this.draggedAsset?.assetInfo?.assetZone?.coordinates!,
    );
    const position = marker.position as google.maps.LatLng;
    const currentZoneName = this.findZone(position, zoneCoordinates);

    if (currentZoneName) {
      const draggedAsset: AssetModel = {
        ...this.draggedAsset,
        assetInfo: {
          ...this.draggedAsset.assetInfo,
          assetZone: {
            area: currentZoneName,
            coordinates: position.toJSON(),
          },
        },
      };

      if (draggedAsset && draggedAsset?.id) {
        this.httpService
          .AddDraftAsset({ siteId: this.site.id!, asset: draggedAsset })
          .subscribe({
            next: () => {
              marker.gmpDraggable = false;
              this.toastService.toastSuccess(
                this.translate.instant("SERVICES_SAVE_TO_UPDATE_LOCATION"),
              );
            },
            error: (err) => {
              this.toastService.toastSuccess(
                this.translate.instant("APP_UPDATE_LOCATION_FAIELD_MESSAGE"),
              );
            },
          });
      }
      this.store.dispatch(ADD_ASSET(draggedAsset));
      this.addShakeAndBackdrop(markerContainer, true);
      markerContainer.removeAttribute("data-long-press-triggered");
    } else {
      marker.position = latlng;
      marker.gmpDraggable = false;
      this.addShakeAndBackdrop(markerContainer, true);
      this.store.dispatch(UPDATE_ASSET(this.draggedAsset));
      markerContainer.style.transition = "transform 0.8s ease";
      markerContainer.removeAttribute("data-long-press-triggered");
      this.toastService.toastWarning(
        this.translate.instant("SERVICES_ASSET_CAN_NOT_MOVE_OUT_OF_ZONE"),
      );
    }
  }

  public handleMarkerDragStart(
    assets: AssetModel[],
    mapMarker: HTMLElement,
    markerContainer: HTMLElement,
  ) {
    const uid = mapMarker.getAttribute("id")!;
    const asset = this.GetAsset(uid, assets);
    this.store.dispatch(REMOVE_ASSET(asset));
    this.draggedAsset = asset;
  }

  // ** Long Press Handler ( Marker )
  public addCustomLongPressHandler(
    element: HTMLElement,
    onLongPress: (e: Event) => void,
    longPressDelay: number,
    tolerance: number,
  ) {
    let pressTimer: number | null = null;
    let startX: number, startY: number;

    const startHandler = (e: TouchEvent | MouseEvent) => {
      const touch = (e as TouchEvent).touches?.[0] || (e as MouseEvent);
      startX = touch.clientX;
      startY = touch.clientY;
      pressTimer = window.setTimeout(() => {
        pressTimer = null;
        onLongPress(e);
      }, longPressDelay);
    };

    const moveHandler = (e: TouchEvent | MouseEvent) => {
      if (!pressTimer) return;
      const touch = (e as TouchEvent).touches?.[0] || (e as MouseEvent);
      if (
        Math.abs(touch.clientX - startX) > tolerance ||
        Math.abs(touch.clientY - startY) > tolerance
      ) {
        clearTimeout(pressTimer);
        pressTimer = null;
      }
    };

    const endHandler = () => {
      if (pressTimer) {
        clearTimeout(pressTimer);
        pressTimer = null;
      }
    };

    element.addEventListener("mouseup", endHandler, { passive: true });
    element.addEventListener("touchend", endHandler, { passive: true });
    element.addEventListener("touchmove", moveHandler, { passive: true });
    element.addEventListener("mousemove", moveHandler, { passive: true });
    element.addEventListener("mousedown", startHandler, { passive: true });
    element.addEventListener("touchstart", startHandler, { passive: true });
  }

  // **  Shake transition ( Marker )
  public addShakeAndBackdrop(
    element: HTMLElement,
    isRemoveShakeandBackdrop: boolean,
  ) {
    if (!isRemoveShakeandBackdrop) {
      const animation = `
        @keyframes quick-shake {
          0% { transform: translateX(0); }
          20% { transform: translateX(-2px); }
          40% { transform: translateX(2px); }
          60% { transform: translateX(-2px); }
          80% { transform: translateX(2px); }
          100% { transform: translateX(0); }
        }
      `;

      const styleSheet = document.createElement("style");
      styleSheet.type = "text/css";
      styleSheet.innerText = animation;
      document.head.appendChild(styleSheet);

      element.style.transition = "transform 0.3s ease-in-out";
      element.style.position = "relative";

      this.backdrop = document.createElement("div");
      this.backdrop.className = "shake-this";
      Object.assign(this.backdrop.style, {
        top: "50%",
        left: "50%",
        zIndex: "-1",
        width: "80px",
        height: "80px",
        borderRadius: "50%",
        position: "absolute",
        pointerEvents: "none",
        transform: "translate(-50%, -50%)",
        backgroundColor: "rgba(0, 123, 255, 0.3)",
        boxShadow: "0 0 10px 5px rgba(0, 123, 255, 0.4)",
      });

      element.appendChild(this.backdrop);
      element.style.transform = "scale(1.2)";
      element.style.animation = "quick-shake 0.3s ease-in-out";
    } else {
      setTimeout(() => {
        element.style.animation = "";
        element.style.transform = "scale(1)";
        const removeBackdrop = element.querySelector(".shake-this");
        if (removeBackdrop?.parentNode) {
          removeBackdrop.parentNode.removeChild(removeBackdrop);
        }
      }, 300);
    }
  }

  // ** Method: Smooth Zoom and Pan Map
  public smoothZoomAndPan(
    targetZoom: number,
    map: google.maps.Map,
    targetBounds: google.maps.LatLngBounds,
  ) {
    map.setZoom(targetZoom);
    map.panTo(targetBounds.getCenter());
  }

  // ** Method: Get Polygon Bounds
  public getPolygonBounds(
    polygon: google.maps.Polygon,
  ): google.maps.LatLngBounds {
    const bounds = new google.maps.LatLngBounds();
    polygon
      .getPath()
      .forEach((latLng: google.maps.LatLng) => bounds.extend(latLng));
    return bounds;
  }

  // ** Method:: Delete Marker
  DeleteMarker(
    assetId: string,
    markers: google.maps.marker.AdvancedMarkerElement[],
  ): void {
    const markerIndex = markers.findIndex((marker) => {
      const markerContent = marker.content as HTMLDivElement;
      const markerId = markerContent
        ?.querySelector("div[id]")
        ?.getAttribute("id");
      return markerId === assetId;
    });
    const mapMarker = document.getElementById(assetId);
    if (mapMarker) {
      this.cleanupCrossMarker(mapMarker);
      mapMarker.remove();
    }
    if (markerIndex !== -1) {
      markers[markerIndex].map = null;
      markers.splice(markerIndex, 1);
    }
  }

  // ** Method:: Clean Up Cross marker
  cleanupCrossMarker(mapMarker: HTMLElement) {
    const crossElement = mapMarker.querySelector(".markerCross") as HTMLElement;
    if (!crossElement) return;

    const uid = mapMarker.getAttribute("id");
    if (!uid) return;

    const listener = this.crossMarkerListeners.get(uid);
    if (listener) {
      crossElement.removeEventListener("click", listener);
      this.crossMarkerListeners.delete(uid);
    }
  }

  // ** API: Get All Master Assets
  public async GetAllMasterAsset(site: SiteModel) {
    const loading = await this.loadingCtrl.create({ spinner: "bubbles" });
    loading.present();
    this.store
      .select("categories")
      .pipe(takeUntil(this.destroy$))
      .subscribe((_categories) => (this.bogProModules = _categories));
    this.httpService
      .GetAllMasterAsset({
        location: site.locations?.country?.location,
        unique_id: site.locations?.country?.unique_id,
        sap_key_code: site.locations?.site?.sap_key_code,
      })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: MasterAssetResponseModel) => {
          if (!response?.status) return;

          // Reset SAP asset count
          this.sapAssetCount = {};
          response?.data?.sap_assets?.forEach(
            (item) =>
              (this.sapAssetCount[item?.type ?? ""] =
                item?.assets?.length ?? 0),
          );

          // Map master assets with SAP counts
          const allMasterAssets = response?.data?.assets?.map((item) => ({
            ...item,
            total_sap_assets:
              (item?.assetInformation?.type &&
                this.sapAssetCount[item?.assetInformation?.type]) ??
              0,
          })) as MasterAsset[];
          const hasModules = getCurrentBogProModuleMap(
            this.bogProModules,
            this.router.url,
          );

          const department = site?.locations?.site?.segment?.department ?? "";

          const { masterAssets, groupedMasterAssets } = processMasterAssets(
            allMasterAssets,
            hasModules,
            department,
            this.siteSegmentsList ?? [],
          );

          this.masterAssets.next(masterAssets);
          this.groupedMasterAssets.next(groupedMasterAssets);
          this.sapAssetsMaster.next(response?.data?.sap_assets);
        },
        complete: () => loading.dismiss(),
        error: () => loading.dismiss(),
      });
  }

  // ** Method: Get Other Master Assets for Selected Asset
  public GetOthersMasterAssets(asset: AssetModel) {
    const masterAssets = this.masterAssets.getValue();
    const hasModules = getCurrentBogProModuleMap(
      this.bogProModules,
      this.router.url,
    );
    const others = masterAssets
      .filter((m) => asset.assetInfo?.assetType === m.assetInformation?.type)
      .flatMap(
        (m) =>
          m.others
            ?.sort((a, b) =>
              (a?.title ?? "")
                .toLowerCase()
                .localeCompare((b?.title ?? "").toLowerCase()),
            )
            ?.map((o) => ({
              ...o,
              total_sap_assets: o?.type ? (this.sapAssetCount[o.type] ?? 0) : 0,
            }))
            ?.filter((recommendedItem) =>
              recommendedItem?.subType?.some((type) =>
                type.category?.some((category) => hasModules.has(category)),
              ),
            ) ?? [],
      );

    // Always update others, even when empty — prevents stale data from a
    // previous asset type persisting when switching to a type with no others.
    this.others.next(others);
  }

  // ** Method: Get Recommended Master Assets for Selected Asset
  public GetRecommendedMasterAssets(asset: AssetModel) {
    let masterAssets = this.masterAssets.getValue();
    const hasModules = getCurrentBogProModuleMap(
      this.bogProModules,
      this.router.url,
    );
    const recommended = masterAssets
      .filter((m) => asset.assetInfo?.assetType === m.assetInformation?.type)
      .flatMap(
        (m) =>
          m.recommended
            ?.sort((a, b) =>
              (a.title ?? "")
                .toLowerCase()
                .localeCompare((b.title ?? "").toLowerCase()),
            )
            ?.map((r) => ({
              ...r,
              total_sap_assets: r?.type ? (this.sapAssetCount[r.type] ?? 0) : 0,
            })) 
            ?.filter((recommendedItem) =>
              recommendedItem?.subType?.some((type) =>
                type.category?.some((category) => hasModules.has(category)),
              ),
            ) ?? [],
      );

    //if (recommended && recommended?.length) 
    this.recommended.next(recommended);
  }

  public findSiteBoundaryByCoordinates(
    latLng: google.maps.LatLng,
    siteCoordinates: SiteFencingModel[],
    sitePolygons: google.maps.Polygon[],
  ): string {
    if (!sitePolygons || !siteCoordinates) return "";
    let siteId = "";

    sitePolygons.forEach((polygon) => {
      if (google.maps.geometry.poly.containsLocation(latLng, polygon)) {
        // Find the matching site boundary by comparing polygon paths
        const site = siteCoordinates.find((siteCoord) => {
          const polygonPaths = polygon.getPath().getArray();
          const sitePaths = siteCoord.coordinates.map(
            (coord) => new google.maps.LatLng(coord.lat, coord.lng),
          );
          return (
            polygonPaths.length === sitePaths.length &&
            polygonPaths.every((p, i) => p.equals(sitePaths[i]))
          );
        });

        if (site) {
          siteId = site.id || "";
        }
      }
    });

    return siteId;
  }
}
